import { Object3D } from 'three';

import { useDollhouse } from '@spazious/dollhouse-engine';
import {
  FNode,
  FNodeTypesEnum,
  getNodeFromObject,
  getStackingParentSelector,
} from '@spazious/dollhouse-fsetup';
import { FShallowNode, FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useStore } from '@spazious/dollhouse-store';
import { traverseUntil } from '@spazious/dollhouse-utils';
import { useEventCallback } from '@spazious/react-hooks';

const findObjectBySelector = (scene: Object3D, selector: string): Object3D | null => {
  let found: null | Object3D = null;
  traverseUntil<Object3D>(scene, (child) => {
    if (getNodeFromObject(child)?.selector === selector) {
      found = child;
      return false;
    }
  });

  return found;
};

const findObjectsBySelectors = (scene: Object3D, selectors: string[]): Record<string, Object3D> => {
  const objects: Record<string, Object3D> = {};
  const pendingSelectors = [...selectors];
  traverseUntil<Object3D>(scene, (child) => {
    if (pendingSelectors.length === 0) {
      return false;
    }
    const selector = getNodeFromObject(child)?.selector;
    if (selector) {
      const index = pendingSelectors.indexOf(selector);
      if (index > -1) {
        pendingSelectors.splice(index, 1);
        objects[selector] = child;
      }
    }
  });

  return objects;
};

const getStackedNodeSelectors = (
  shallowNodes: {
    [selector: string]: FShallowNode;
  },
  parentSelector: string
) => {
  return Object.entries(shallowNodes).reduce((acc, [selector, shallow]) => {
    if (shallow.type !== FShallowNodeTypesEnum.FGroup && shallow.parent === parentSelector) {
      return [...acc, selector];
    }
    return acc;
  }, [] as string[]);
};

export const useSelectionReducer = () => {
  const scene = useDollhouse(({ scene }) => scene);
  const shallowNodes = useStore(({ graph }) => graph.shallow.nodes);

  /**
   * Get all stacked nodes on top of the
   * - current object
   * - or the parent on which object is stacked
   * - including current object
   */
  const getStackedRelatedObjects = useEventCallback((obj: Object3D) => {
    const node = getNodeFromObject(obj) as FNode;
    const result = new Set<Object3D>([]);
    const parentSelector = getStackingParentSelector(node) || node.selector;
    const stackedSelectors = getStackedNodeSelectors(shallowNodes, parentSelector);
    if (stackedSelectors.length > 0) {
      Object.values(findObjectsBySelectors(scene, [parentSelector, ...stackedSelectors])).forEach(
        (obj) => result.add(obj)
      );
    } else {
      result.add(obj); // no stacking relation, just select this node
    }
    return { parentSelector, objects: Array.from(result), isStacked: stackedSelectors.length > 0 };
  });

  /**
   *
   * Selection Logic
   * - if no new items, just reset selection
   * - no shift, single click
   *  - if furnitures are stacked
   *    - on 1st click, select full stack
   *    - on next click select originator(if present) only
   *    - on next click select individual furniture only
   *  - else
   *    - if nothing selected yet and object has originator
   *      - select originator
   *    - else
   *      - unselect already selected objects
   *      - select current object
   * - shift click
   *   - if captured object already selected
   *     - unselect current object
   *   - else
   *     - select current object
   *     - select stacked-related objects
   * - multiselect box
   *   - unselect previously selected objects
   *   - select current captured objects
   *
   */
  const selectionReducer = useEventCallback(
    (
      selected: Object3D[],
      items: Object3D[],
      shift: boolean = false,
      selectOriginator: boolean = true
    ): Object3D[] => {
      if (items.length === 0) {
        return []; // unselect all
      }

      const isMultiSelect = items.length > 1;

      if (selectOriginator && !shift && !isMultiSelect) {
        // single click selection

        const item = items[0];
        const node = getNodeFromObject(item) as FNode;

        const { parentSelector, isStacked, objects: stacked } = getStackedRelatedObjects(item);
        if (isStacked) {
          // select entire stack if none selected
          if (stacked.every((item) => !selected.includes(item))) {
            return stacked;
          }
          if (parentSelector === node.selector && !selected.includes(item)) {
            // current node is the stack parent and it is not selected yet
            // so select the whole stack
            return stacked;
          }
          // next if full stack was selected,
          // select originator if found
          const fullStackSelected = stacked.every((item) => selected.includes(item));
          if (fullStackSelected && node?.type === FNodeTypesEnum.Furniture && node.originator) {
            const found = findObjectBySelector(scene, node.originator!.selector);
            if (found) {
              return [found];
            }
          }
          // else current object
          return [item];
        }

        // not stacked
        // select originator if nothing selected already
        if (selected.length === 0) {
          if (node?.type === FNodeTypesEnum.Furniture && node.originator) {
            const found = findObjectBySelector(scene, node.originator!.selector);
            if (found) {
              return [found];
            }
          }
        }

        return [item]; // select only current item
      } else if ((!selectOriginator || shift) && !isMultiSelect) {
        const item = items[0];
        if (selected.includes(item)) {
          // unselect this item
          return selected.filter((obj) => obj !== item);
        } else {
          // keep the already selected items as is, and add new items

          let newItems = [item];
          if (selectOriginator) {
            // stacked objects logic for additions
            const { objects: stacked } = getStackedRelatedObjects(item);
            newItems = stacked.every((item) => !selected.includes(item)) ? stacked : [item];
          }

          return Array.from(new Set<Object3D>([...selected, ...newItems]));
        }
      } else {
        // multi selection, select the latest batch alone
        return items;
      }
    }
  );

  return selectionReducer;
};
